import React, { useMemo } from 'react'
import { Button, Grid, Typography, Divider } from '@material-ui/core'
import {
  MigrationCollections,
  MigrationStatus,
  CompanyShopsAndEmployeesMigrationResponse,
} from '../../../types/migration'
import { ReactSelectOption } from '../../../types'
import styled from 'styled-components'
import { MigrationChipStatus } from './MigrationChipStatus.tsx'
import { CustomModal } from '../../../components/common/CustomModal'
import { CompanyCollectionDetailsContent } from './MigrateCompanyProducts/CompanyCollectionDetailsModal/CompanyCollectionDetailsContent'
import { unsyncedStatuses } from './MigrateCompanyProductsPage'
import { MigrationAlert } from './MigrateCompanyProducts/styled'

const Section = styled.div({
  marginBottom: '16px',
})

const SectionTitle = styled(Typography)({
  fontWeight: 'bold',
  marginBottom: '8px',
})

const CollectionList = styled.div({
  marginLeft: '16px',
  marginBottom: '8px',
})

const ItemContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  height: '24px',
})

const ShopCollectionsWrapper = styled.div<{ disabled: boolean }>(({ disabled }) => ({
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
}))

export interface IBaseCollection {
  name: MigrationCollections
  status: MigrationStatus
  created?: string
}

export interface ShopCollections {
  shop: ReactSelectOption
  collections: IBaseCollection[]
}

export interface CompanyCollections {
  collections: IBaseCollection[]
}

interface SyncConfirmationModalProps {
  open: boolean
  onClose: () => void
  onSyncMissingCollections: () => void
  onSyncAll: () => void
  shopCollections: ShopCollections[]
  companyCollections: CompanyCollections
  selectedCompany?: ReactSelectOption
  companyMigrationDetails?: CompanyShopsAndEmployeesMigrationResponse
  selectedShops?: ReactSelectOption[]
  onSyncCompanyData?: () => void
}

export const SyncConfirmationModal: React.FC<SyncConfirmationModalProps> = ({
  open,
  onClose,
  onSyncMissingCollections,
  onSyncAll,
  shopCollections,
  companyCollections,
  selectedCompany,
  companyMigrationDetails,
  selectedShops,
  onSyncCompanyData,
}) => {
  const syncedLocationIds = companyMigrationDetails?.data?.syncedShopIds

  const missingShops = useMemo(() => {
    if (!selectedShops || !syncedLocationIds) return []
    return selectedShops.filter((shop) => !syncedLocationIds.includes(shop.value))
  }, [selectedShops, syncedLocationIds])

  const hasMissingCollections = useMemo(() => {
    const hasMissingShopCollections = shopCollections.some((shopCollection) =>
      shopCollection.collections.some(
        (collection) => unsyncedStatuses.includes(collection.status) || !collection.status,
      ),
    )

    const hasMissingCompanyCollections = companyCollections.collections.some(
      (collection) => unsyncedStatuses.includes(collection.status) || !collection.status,
    )

    return hasMissingShopCollections || hasMissingCompanyCollections
  }, [shopCollections, companyCollections])

  const isMissingShops = missingShops.length > 0 || companyMigrationDetails === undefined
  const filteredShopCollections = shopCollections.filter(
    (shopCollection) => shopCollection.collections.length > 0,
  )
  return (
    <CustomModal open={open} onClose={onClose} size="medium">
      <CustomModal.Header
        title={
          <span>
            Collections Status{' '}
            {!!isMissingShops && (
              <MigrationAlert variant="caption">
                All shops need to be synced before exporting collections
              </MigrationAlert>
            )}
          </span>
        }
      />
      <CustomModal.Content>
        <Grid container spacing={8}>
          <Grid item xs={12}>
            {companyCollections.collections.length > 0 && (
              <>
                <div
                  style={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  {companyCollections.collections.some(
                    (c) =>
                      c.name === MigrationCollections.Company ||
                      c.name === MigrationCollections.Shops ||
                      c.name === MigrationCollections.Employees,
                  ) && (
                    <CompanyCollectionDetailsContent
                      selectedCompany={selectedCompany}
                      companyMigrationDetails={companyMigrationDetails}
                      selectedShops={selectedShops}
                      onSyncCompanyData={onSyncCompanyData}
                    />
                  )}

                  {companyCollections.collections
                    .filter(
                      (c) =>
                        c.name === MigrationCollections.Brands ||
                        c.name === MigrationCollections.Vendors,
                    )
                    .map((collection) => (
                      <Section key={collection.name}>
                        <ItemContainer>
                          <Typography style={{ width: 250, fontWeight: 'bold' }}>
                            {collection.name}
                          </Typography>
                          <MigrationChipStatus
                            time={
                              collection.created
                                ? new Date(collection.created).getTime()
                                : undefined
                            }
                            status={collection.status}
                          />
                        </ItemContainer>
                      </Section>
                    ))}
                </div>
                <Divider style={{ margin: '16px 0' }} />
              </>
            )}

            {filteredShopCollections.length > 0 ? (
              <ShopCollectionsWrapper disabled={missingShops.length > 0}>
                <div
                  id="shop-collections"
                  style={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  {
                    <Section>
                      {filteredShopCollections.map((shopData, index) => {
                        return (
                          <Section key={shopData.shop.value}>
                            <SectionTitle>{shopData.shop.label}</SectionTitle>
                            {shopData.collections.length > 0 ? (
                              <CollectionList>
                                {shopData.collections.map((collection) => {
                                  return (
                                    <ItemContainer
                                      key={`${shopData.shop.value}-${collection.name}`}
                                    >
                                      <Typography style={{ width: 250 }}>
                                        {collection.name}
                                      </Typography>
                                      <MigrationChipStatus
                                        time={
                                          collection.created
                                            ? new Date(collection.created).getTime()
                                            : undefined
                                        }
                                        status={collection.status}
                                      />
                                    </ItemContainer>
                                  )
                                })}
                              </CollectionList>
                            ) : (
                              <Typography style={{ marginTop: '16px', fontStyle: 'italic' }}>
                                No shop collections selected
                              </Typography>
                            )}

                            {index < shopCollections.length - 1 && (
                              <Divider style={{ margin: '16px 0' }} />
                            )}
                          </Section>
                        )
                      })}
                    </Section>
                  }
                </div>
              </ShopCollectionsWrapper>
            ) : (
              <></>
            )}

            <Typography style={{ marginTop: '16px' }}>Do you want to proceed?</Typography>
          </Grid>
        </Grid>
      </CustomModal.Content>
      <CustomModal.Footer>
        <Button onClick={onClose} className="button-white-and-red-padded">
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSyncMissingCollections()
          }}
          disabled={isMissingShops || !hasMissingCollections}
          className="button-blue-and-white-padded"
        >
          Export Missing Collections
        </Button>
        <Button
          onClick={onSyncAll}
          disabled={isMissingShops}
          className="button-blue-and-white-padded"
        >
          Export All
        </Button>
      </CustomModal.Footer>
    </CustomModal>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/SyncConfirmationModal.tsx